/**
 * Stable viewer + session identifiers.
 * One key written, the SAME key read (v1 wrote `session_id_<embedId>` but read
 * `session_id`, so persistence never worked).
 */

const VIEWER_KEY = 'lp_viewer_id'

/** Used only when the server did not say when the session expires. */
const DEFAULT_SESSION_TTL_MS = 12 * 60 * 60 * 1000

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function storageGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function storageSet(storage: Storage | undefined, key: string, value: string): boolean {
  try {
    storage?.setItem(key, value)
    return storage?.getItem(key) === value
  } catch {
    return false
  }
}

let memoryViewerId: string | null = null

/** Durable per-browser viewer id (used for Mux + viewer analytics). */
export function getViewerId(): string {
  const fromLocal = storageGet(window.localStorage, VIEWER_KEY)
  if (fromLocal) return fromLocal
  const fromSession = storageGet(window.sessionStorage, VIEWER_KEY)
  if (fromSession) return fromSession
  if (memoryViewerId) return memoryViewerId

  const id = generateUUID()
  if (storageSet(window.localStorage, VIEWER_KEY, id)) return id
  if (storageSet(window.sessionStorage, VIEWER_KEY, id)) return id
  memoryViewerId = id
  return id
}

function sessionKey(embedId: string): string {
  return `lp_session_${embedId}`
}

interface StoredSession {
  id: string
  /** epoch ms; the server's `expiresAt`, or a conservative default */
  exp: number
}

/**
 * Sessions are remembered ACROSS tabs and restarts, always, with no viewer-facing
 * option — a returning viewer of a password-protected stream is not asked again
 * while the grant is alive.
 *
 * What is stored is an opaque session id, never the password and never a
 * credential: the grant itself lives in watch-guard against this id, so it can
 * be revoked server-side and it expires on its own. localStorage is preferred
 * (survives restarts); sessionStorage is the fallback when it is unavailable —
 * private windows, blocked site data — where "remembered" degrades to the tab.
 */
export function recallSession(embedId: string): string | null {
  const key = sessionKey(embedId)
  const raw = storageGet(window.localStorage, key) ?? storageGet(window.sessionStorage, key)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (typeof parsed.id !== 'string' || typeof parsed.exp !== 'number') return null
    if (parsed.exp <= Date.now()) {
      forgetSession(embedId)
      return null
    }
    return parsed.id
  } catch {
    return null
  }
}

/** Store the server's session id. `expiresAt` is the ISO string it returned. */
export function rememberSession(embedId: string, sessionId: string, expiresAt?: string | null): void {
  const parsed = expiresAt ? Date.parse(expiresAt) : NaN
  const exp = Number.isFinite(parsed) ? parsed : Date.now() + DEFAULT_SESSION_TTL_MS
  const value = JSON.stringify({ id: sessionId, exp } satisfies StoredSession)
  const key = sessionKey(embedId)
  if (storageSet(window.localStorage, key, value)) return
  storageSet(window.sessionStorage, key, value)
}

/** Drop the remembered session — expiry, or a denial that invalidates it. */
export function forgetSession(embedId: string): void {
  const key = sessionKey(embedId)
  try {
    window.localStorage?.removeItem(key)
  } catch {
    /* storage unavailable */
  }
  try {
    window.sessionStorage?.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

/**
 * Session id for this embed. The SERVER's value always wins and overwrites what
 * is stored — a remembered id is only a proposal to rejoin, and watch-guard is
 * free to hand back a different one.
 */
export function getSessionId(embedId: string, fromServer?: string | null, expiresAt?: string | null): string {
  if (fromServer) {
    rememberSession(embedId, fromServer, expiresAt)
    return fromServer
  }

  const remembered = recallSession(embedId)
  if (remembered) return remembered

  // No server value yet (pre-authorize analytics): keep a local id so viewer
  // pings correlate, and let the server replace it the moment it answers.
  const id = generateUUID()
  rememberSession(embedId, id, null)
  return id
}
